<template>
  <div class="entity host">
    <h3 class="ui header">Stack</h3>
    <sui-segment emphasis="secondary" :color="hasStackError ? 'red' : 'green'" :loading="templateLoading">
      <sui-form-fields>
        <sui-form-field :width="9">
          <label>Template</label>
          <sui-dropdown v-model="template" search selection :options="stackList" placeholder="Select..." />
        </sui-form-field>
        <EntityDropdown v-model="entity.project" fieldName="project" :options="projectList" />
        <sui-form-field :width="2">
          <label style="opacity:0">.</label>
          <div class="ui tiny fluid compact icon button" :class="{disabled: !access.projectCreate}" data-inverted data-position="left center" data-tooltip="Create new project" data-goal="STACK_FORM_NEW_PROJECT" @click.prevent="$modals.createProject.open({}, onCreateProject)">
            <i class="ui large fitted icons">
              <i class="folder fitted icon" />
              <i class="inverted corner plus fitted icon" />
            </i>
          </div>
        </sui-form-field>
      </sui-form-fields>
      <sui-form-fields>
        <EntityInput v-model="url" fieldName="url" />
        <sui-form-field :width="3">
          <label>Archive</label>
          <div>
            <label class="ui fluid right labeled icon button example upload" :class="{loading: uploading, active: isUploaded}">
              <input type="file" ref="file" accept=".zip, .tar, .tar.gz, .tar.bz2" @change="upload()"/>
              {{isUploaded ? 'Uploaded' : 'Upload'}}
              <i class="icon" :class="isUploaded ? 'check' : 'upload'" />
            </label>
            <small class="upload-extensions">.zip, .tar, .tar.gz, .tar.bz2</small>
          </div>
        </sui-form-field>
      </sui-form-fields>
      <sui-message class="errors" error size="tiny" v-if="stackError">
        {{stackError}}
      </sui-message>
    </sui-segment>
    <h3 class="ui header">Hosts</h3>
    <sui-segment emphasis="secondary" :class="{disabled: templateLoading}" v-if="!entity.hosts.length">
      No hosts
    </sui-segment>
    <div v-if="entity.hosts.length">
      <FormStackHost v-model="entity.hosts[index]" mode="importing" :class="{disabled: templateLoading, red: simpleEntityErrors.hosts[index], green: !simpleEntityErrors.hosts[index]}" v-for="(host, index) in entity.hosts" :key="entity.url + host.oldName" />
    </div>
    <h3 class="ui header">Services</h3>
    <sui-segment emphasis="secondary" :class="{disabled: templateLoading}" v-if="!entity.services.length">
      No services
    </sui-segment>
    <div v-if="entity.services.length">
      <FormStackService v-model="entity.services[index]" mode="importing" :class="{disabled: templateLoading, red: simpleEntityErrors.services[index], green: !simpleEntityErrors.services[index]}" :data-errorrrr="simpleEntityErrors.services[index]" v-for="(service, index) in entity.services" :key="entity.url + service.name" />
    </div>
  </div>
</template>

<script>
  import {mapGetters} from 'vuex'
  // import pick from 'lodash/pick'
  import debounce from 'lodash/debounce'
  import isUndefined from 'lodash/isUndefined'
  import some from 'lodash/some'
  import FormEntity from '@/mixins/FormEntity'
  import EntityDropdown from '@/components/forms/EntityDropdown'
  import EntityInput from '@/components/forms/EntityInput'
  import FormStackHost from '@/components/forms/StackHost'
  import FormStackService from '@/components/forms/StackService'
  import api from '@/api'
  import d2c from '@/d2c'
  import utils from '@/utils'
  // import objects from '@/objects'

  export default {
    name: 'FormStack',
    mixins: [
      FormEntity,
    ],
    components: {
      FormStackHost,
      FormStackService,
      EntityDropdown,
      EntityInput,
    },
    data() {
      return {
        object: 'Stack',
        commonFields: ['project', 'url'],
        url: '',
        template: null,
        templateLoading: false,
        stackError: '',
        uploading: false,
        isUploaded: false,
      }
    },
    computed: {
      ...mapGetters(['access', 'actionsById', 'config', 'projects']),
      stackList() {
        return d2c.stacks.filter(stack => stack.active).map(stack => {
          return {
            image: {src: stack.img},
            text: stack.title,
            value: stack.repo,
            repo: stack.repo,
          }
        }).concat({
          text: 'Custom stack',
          value: null,
        })
      },
      projectList() {
        return this.projects.map(project => {
          return {
            icon: 'grey folder',
            text: project.name,
            value: project.id,
            disabled: !this.actionsById[project.id].projectUsage,
          }
        })
      },
      entityErrors() {
        return this.entity.getErrors('importing')
      },
      simpleEntityErrors() {
        return {
          hosts: this.entityErrors.hosts.map(errors => some(errors)),
          services: this.entityErrors.services.map(errors => some(errors)),
        }
      },
      hasStackError() {
        return (!this.url && !this.isUploaded) || this.errors.project
      },
    },
    watch: {
      template(template1) {
        utils.reachGoal('STACK_FORM_TEMPLATE')
        if (template1) {
          this.url = template1
          this.entity.url = template1
        }
      },
      url() {
        if (this.url !== this.entity.url) {
          if (this.url) {
            this.debouncedSetUrl(this.url)
          } else {
            this.entity.url = this.url
          }
        }
      },
      'entity.url'() {
        const urlArchive = this.entity.url ? utils.getRepoArchive(this.entity.url) : ''
        const template = this.stackList.find(stack => {
          const templateArchive = stack.value && utils.getRepoArchive(stack.value)
          return templateArchive === this.url || templateArchive === urlArchive
        })
        if (template) {
          this.template = template.value
        } else {
          this.template = null
        }
        this.stackError = ''
        this.isUploaded = false
        this.loadStackLink()
      },
      'entity.project'() {
        this.entity.services.forEach(service => {
          service.project = this.entity.project
        })
      },
      'entity.hosts'() {
        this.assignHosts()
      },
    },
    methods: {
      debouncedSetUrl: debounce(function(url) {
        utils.reachGoal('STACK_FORM_URL')
        this.entity.url = url
      }, 500),
      loadStackLink() {
        if (this.entity.url && !this.entityErrors.url) {
          this.templateLoading = true
          const urlArchive = utils.getRepoArchive(this.entity.url)
          api.importStackLink(urlArchive).then(stackData => {
            this.createEntity(stackData)
            prepareStackData(this.entity)
            this.assignHosts()
          }).catch(error => {
            this.stackError = error.message
            this.createEntity({
              services: [],
              hosts: [],
            })
          }).finally(() => {
            this.templateLoading = false
          })
        } else {
          this.createEntity({
            services: [],
            hosts: [],
          })
        }
      },
      upload() {
        const archive = this.$refs.file.files[0]
        this.url = ''
        this.template = null
        if (!archive) {
          this.stackError = 'Archive is not selected'
          return
        }
        const isArchive = utils.isArchiveUrl(archive.name)
        if (!isArchive) {
          this.stackError = 'Available archive types: .tar.bz2, .tar.gz, .tar, .zip'
          return
        }
        const uploadSize = this.config.uploadSize
        const goodSize = archive.size < uploadSize
        if (!goodSize) {
          this.stackError = 'Maximum archive size: ' + utils.prettyBytes(uploadSize)
          return
        }
        if (archive && isArchive && goodSize) {
          this.uploading = true
          utils.reachGoal('STACK_FORM_UPLOAD_TRY')
          api.importStackFile(archive)
            .finally(() => {
              this.isUploaded = true
              this.uploading = false
              this.$refs.file.value = ''
            })
            .then(stackData => {
              utils.reachGoal('STACK_FORM_UPLOAD_SUCCESS')
              this.createEntity(stackData)
              prepareStackData(this.entity)
            })
            .catch(error => {
              utils.reachGoal('STACK_FORM_UPLOAD_FAIL')
              this.stackError = error.message
              this.createEntity({
                services: [],
                hosts: [],
              })
            })
        }
      },
      onCreateProject(result) {
        if (result && result.id) {
          this.entity.project = result.id
        }
      },
      assignHosts() {
        this.entity.services.forEach(service => {
          if (service.constructor.fields) {
            const [role1, role2] = service.constructor.fields.hosts.selector[service.configuration || 'default']
            const newHosts = []
            service.deployTo.forEach((hostName, index) => {
              const host = this.entity.hosts.find(host => host.newName === hostName)
              if (host && host.id) {
                newHosts.push({
                  id: host.id,
                  role: index ? role2 || role1 : role1,
                })
              }
            })
            service.hosts = newHosts
          }
        })
      },
    },
  }

  function prepareStackData(entity) {
    entity.services.forEach(service => {
      service.project = entity.project
      service.schema = getServiceSchema(service)
      if (service.source === null) {
        delete service.source
      }
      if (service.ports === null) {
        delete service.ports
      }
    })
    entity.hosts.forEach(host => {
      host.newName = host.name
      host.services = entity.services.filter(service => service.deployTo.includes(host.newName))
    })
  }

  function getServiceSchema(service) {
    return {
      buildCommands: {
        hidden: isUndefined(service.buildCommands),
        required: service.buildCommands === null,
        readonly: service.buildCommands !== null,
        disabled: Boolean(service.buildCommands),
      },
      crons: {
        hidden: !service.crons || !service.crons.length,
        omit: !service.crons || !service.crons.length,
      },
      database: {
        hidden: isUndefined(service.database),
        required: service.database === null,
        readonly: service.database !== null,
        disabled: Boolean(service.database),
      },
      deployCommands: {
        hidden: isUndefined(service.deployCommands),
        required: service.deployCommands === null,
        readonly: service.deployCommands !== null,
        disabled: Boolean(service.deployCommands),
      },
      env: {
        hidden: !service.env || !service.env.length,
      },
      password: { // required if configuration ?
        hidden: isUndefined(service.password),
        required: service.password === null,
        readonly: service.password !== null,
        disabled: Boolean(service.password),
      },
      ports: {
        hidden: service.ports !== null && (!service.ports || !service.ports.length),
        readonly: service.ports !== null,
      },
      remoteAccess: {
        readonly: service.remoteAccess !== null,
      },
      source: {
        readonly: service.source !== null,
      },
      startCommand: {
        hidden: isUndefined(service.startCommand),
        required: service.startCommand === null,
        readonly: service.startCommand !== null,
        disabled: Boolean(service.startCommand),
      },
      type: {
        required: false
      },
      username: {
        hidden: isUndefined(service.username),
        required: service.username === null,
        readonly: service.username !== null,
        disabled: Boolean(service.username),
      },
      userPassword: {
        hidden: isUndefined(service.userPassword),
        required: service.userPassword === null,
        readonly: service.userPassword !== null,
        disabled: Boolean(service.userPassword),
      },
    }
  }
</script>

<style scoped>
  h4 { margin-bottom: .5em!important; }
  .upload-extensions { color: #aaa; }
  .errors { display: block!important; margin-top: -1em; color: #9f3a38; }
</style>
